import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import { modifier } from 'ember-modifier';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';

const DEFAULT_WIDTH = 400;

/**
 * Component glue shared by the canvas charts: container measurement, resize
 * redraws, pointer listeners and the hover/tooltip state their templates read.
 * Subclasses draw and hit-test; apply `installCanvas` to the element that wraps
 * the `<canvas>`.
 */
export default abstract class CanvasChart<S extends { Args: object }> extends Component<S> {
  @tracked containerWidth = DEFAULT_WIDTH;
  @tracked containerHeight = 0;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;

  /** Height in px when neither `@height` nor a measured container height applies. */
  protected defaultHeight = 300;

  /** Record the container's height on insert and resize, so `height` can follow it. */
  protected measuresContainerHeight = true;

  /** Take the first measurement on the next run-loop tick rather than on insert. */
  protected measuresOnNextTick = false;

  /**
   * Canvas height in px: `@height` when set, else the measured container height,
   * else `defaultHeight`. Charts sized another way override this getter.
   */
  get height(): number {
    const { height } = this.args as { height?: number };
    if (height) return height;
    return this.containerHeight > 0 ? this.containerHeight : this.defaultHeight;
  }

  /**
   * Paints the whole chart for the current size and hover state.
   * @param canvas the chart's canvas, already in the DOM
   */
  abstract draw(canvas: HTMLCanvasElement): void;

  /**
   * Updates `hoveredIndex` and the tooltip position for a pointer move, redrawing
   * when the hovered item changes.
   * @param event the canvas `mousemove` event
   * @param canvas the chart's canvas
   */
  abstract handleMouseMove(event: MouseEvent, canvas: HTMLCanvasElement): void;

  /**
   * Handles a click on the canvas. Only charts that override it get a click listener.
   * @param event the canvas `click` event
   * @param canvas the chart's canvas
   */
  handleClick(_event: MouseEvent, _canvas: HTMLCanvasElement) {}

  /**
   * Clears the hover when the pointer leaves the canvas and redraws.
   * @param canvas the chart's canvas
   */
  handleMouseLeave(canvas: HTMLCanvasElement) {
    this.hoveredIndex = null;
    this.draw(canvas);
  }

  /**
   * Reads every value whose change should re-install the canvas (and so redraw).
   * Called inside the modifier before anything else; the default reads nothing.
   */
  trackRedrawInputs() {}

  installCanvas = modifier((element: HTMLElement) => {
    this.trackRedrawInputs();

    const canvas = element.querySelector('canvas');
    if (!canvas) return;

    const observer = createDebouncedResizeObserver((entry) => {
      this.containerWidth = entry.contentRect.width;
      if (this.measuresContainerHeight && entry.contentRect.height > 0) {
        this.containerHeight = entry.contentRect.height;
      }
      this.draw(canvas);
    });
    observer.observe(element);

    const measure = () => {
      this.containerWidth = element.clientWidth || DEFAULT_WIDTH;
      if (this.measuresContainerHeight) {
        this.containerHeight = element.clientHeight || 0;
      }
      this.draw(canvas);
    };
    if (this.measuresOnNextTick) {
      // eslint-disable-next-line ember/no-runloop -- one-shot post-insert measure scheduled for the next tick; tied to the element's lifetime
      next(this, measure);
    } else {
      measure();
    }

    const onMove = (e: MouseEvent) => this.handleMouseMove(e, canvas);
    const onLeave = () => this.handleMouseLeave(canvas);
    const onClick = (e: MouseEvent) => this.handleClick(e, canvas);
    const listensForClicks = this.handleClick !== CanvasChart.prototype.handleClick;
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    if (listensForClicks) canvas.addEventListener('click', onClick);

    return () => {
      observer.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      if (listensForClicks) canvas.removeEventListener('click', onClick);
    };
  });
}
